import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
// ISO dates ('yyyy-MM-dd') are parsed and formatted in the same (local) frame, so calendar-day
// arithmetic on them is unaffected by DST or the runtime's time zone.
export function isoDateFromTimestamp(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
export function timestampFromIsoDate(isoDate) {
    return new Date(`${isoDate}T00:00:00Z`).getTime();
}
export function addDaysToIsoDate(isoDate, days) {
    return format(addDays(parseISO(isoDate), days), ISO_DATE_FORMAT);
}
export function daysBetweenIsoDates(from, to) {
    return differenceInCalendarDays(parseISO(to), parseISO(from));
}
export function daysBetweenTimestamps(from, to) {
    return daysBetweenIsoDates(isoDateFromTimestamp(from), isoDateFromTimestamp(to));
}
/**
 * Cycles can be individually moved (Edit protocol dialog) and are no longer necessarily evenly
 * spaced from the protocol's startDate, so a known cycleStartDates entry is the source of truth;
 * the day-repetition formula is only a fallback for a cycle with no recorded date yet.
 */
export function resolveCycleStartDate(patientProtocol, cycle, daysRepetition) {
    const known = patientProtocol.cycleStartDates?.[cycle];
    if (known)
        return timestampFromIsoDate(known);
    return timestampFromIsoDate(addDaysToIsoDate(isoDateFromTimestamp(patientProtocol.startDate), (cycle - 1) * daysRepetition));
}
// The earliest date a prescription of the protocol may have.
export function getFirstCycleStartDate(patientProtocol) {
    return resolveCycleStartDate(patientProtocol, 1, 0);
}
export function computeTherapyDateFromCycleDay(patientProtocol, cycle, day, daysRepetition, dayZeroProtocol) {
    const cycleStart = isoDateFromTimestamp(resolveCycleStartDate(patientProtocol, cycle, daysRepetition));
    return timestampFromIsoDate(addDaysToIsoDate(cycleStart, dayZeroProtocol ? day : day - 1));
}
/**
 * Inverse of computeTherapyDateFromCycleDay: anchors to the latest known cycle starting on or
 * before the given date. A date past every known cycle (protocol still open, no further cycles
 * scheduled yet) just lands on that last known cycle with a larger day offset, instead of
 * extrapolating a hypothetical future cycle from the (no longer reliable) day-repetition formula.
 */
export function computeCycleDayFromTherapyDate(patientProtocol, therapyDate, daysRepetition, dayZeroProtocol) {
    const therapyIso = isoDateFromTimestamp(therapyDate);
    // ISO dates compare correctly as strings.
    const anchor = Object.entries(patientProtocol.cycleStartDates ?? {})
        .map(([cycle, startIso]) => ({ cycle: Number(cycle), startIso }))
        .filter(entry => entry.startIso <= therapyIso)
        .reduce((latest, entry) => (!latest || entry.startIso > latest.startIso ? entry : latest), undefined);
    // No known cycle starts on/before the date at all (legacy protocol with no cycleStartDates
    // recorded yet, or a date before the protocol even started) - fall back to the even
    // day-repetition formula rather than pinning everything to cycle 1.
    if (!anchor) {
        const totalOffsetDays = daysBetweenIsoDates(isoDateFromTimestamp(patientProtocol.startDate), therapyIso);
        const cycleIndex = Math.floor(totalOffsetDays / daysRepetition);
        const dayInCycle = totalOffsetDays - cycleIndex * daysRepetition;
        return { cycle: cycleIndex + 1, day: dayZeroProtocol ? dayInCycle : dayInCycle + 1 };
    }
    const offsetDays = daysBetweenIsoDates(anchor.startIso, therapyIso);
    return { cycle: anchor.cycle, day: dayZeroProtocol ? offsetDays : offsetDays + 1 };
}
//# sourceMappingURL=protocol-cycles.js.map